import React from "react";
import { motion } from "framer-motion";

import { AppWrap, MotionWrap } from "../../wrapper";
import { calculateTotalExperience } from "../../utils";
import "./About.scss";

const PILLARS = [
  {
    title: "Turning Ambiguity Into Action",
    body: "I enjoy taking rough ideas, asking the right questions, identifying trade-offs, and turning uncertainty into a plan that can actually be executed.",
    accent: "var(--secondary-color)",
  },
  {
    title: "Engineering Beyond Code",
    body: "The best technical decisions are often business decisions in disguise. I've learned that the most valuable engineers understand both.",
    accent: "#a78bfa",
  },
  {
    title: "Building for the Long Run",
    body: "I'm drawn to environments where ownership matters — startups, zero-to-one products, and fast-moving teams that require curiosity and adaptability.",
    accent: "#818cf8",
  },
];

const BULLETS = [
  "Product startups across growth stages",
  "Engineering + product thinking",
  "AI-powered experiences & reliability",
  "Close to the customer, close to the impact",
];

const About = () => {
  const years = calculateTotalExperience(1, 2022);

  return (
    <div className="app__about-split">
      <motion.div
        className="app__about-left"
        initial={{ opacity: 0, x: -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <span className="app__about-eyebrow">About</span>
        <h2 className="app__about-headline">
          What <span>Drives</span> Me
        </h2>
        <p className="app__about-intro">
          I love the moment when an idea goes from a conversation to something
          real &amp; impactful. That's what keeps me interested in engineering.
        </p>

        <div className="app__about-stats">
          <div className="app__about-stat">
            <span className="app__about-stat-num">
              {years}<span className="accent">+</span>
            </span>
            <span className="app__about-stat-label">Years Experience</span>
          </div>
          <div className="app__about-stat">
            <span className="app__about-stat-num app__about-stat-num--sm">
              0<span className="accent">→</span>1
            </span>
            <span className="app__about-stat-label">to Scaling</span>
          </div>
          <div className="app__about-stat">
            <span className="app__about-stat-num">∞</span>
            <span className="app__about-stat-label">Curiosity</span>
          </div>
        </div>

        <ul className="app__about-bullets">
          {BULLETS.map((b) => (
            <li key={b} className="app__about-bullet">
              <span className="app__about-bullet-dot" />
              {b}
            </li>
          ))}
        </ul>
      </motion.div>

      <div className="app__about-right">
        {PILLARS.map((pillar, i) => (
          <motion.div
            key={pillar.title}
            className="app__about-pillar"
            style={{ "--pillar-accent": pillar.accent }}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: i * 0.12, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h3>{pillar.title}</h3>
            <p>{pillar.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AppWrap(MotionWrap(About, "app__about"), "about", "app__whitebg");
